/**
 * AiCloud Settings - Hesap ayarlari, tier bilgisi, disconnect
 */

import axios from 'axios'
import { AlertCircle, Check, Download, Settings, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { API_URL } from '../../config/api'

interface AiCloudStatus {
  tier: string
  provider: string | null
  status: string
  limits: {
    projects: number
    tasks_per_month: number
    tokens_per_month: number
  }
}

export default function AiCloudSettingsPage() {
  const [status, setStatus] = useState<AiCloudStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [disconnecting, setDisconnecting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem('token')
        const resp = await axios.get(`${API_URL}/api/aicloud/my-status`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        setStatus(resp.data as AiCloudStatus)
      } catch {
        setStatus(null)
      } finally {
        setLoading(false)
      }
    }
    void fetch()
  }, [])

  const handleDisconnect = async () => {
    setShowConfirm(false)
    setDisconnecting(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.delete(`${API_URL}/api/aicloud/disconnect`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setSuccess('AiCloud disconnected successfully')
      setStatus(null)
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to disconnect')
    } finally {
      setDisconnecting(false)
    }
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Settings</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>AiCloud account settings and configuration.</p>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}
      {success && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '8px', color: '#10b981', fontSize: '13px', marginBottom: '16px' }}>
          <Check size={14} /> {success}
        </div>
      )}

      {/* Confirm modal */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ padding: '24px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '12px', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '12px' }}>Disconnect AiCloud?</h3>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px', lineHeight: 1.6 }}>
              Your cloud resources will NOT be deleted. Only the connection between RunAI and your cloud account will be removed.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => { void handleDisconnect() }}
                style={{ padding: '8px 16px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                Disconnect
              </button>
              <button onClick={() => setShowConfirm(false)}
                style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {status && (
            <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                <Settings size={16} color="#3b82f6" />
                <span style={{ fontSize: '14px', fontWeight: 600 }}>Current Plan</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                {[
                  { label: 'Tier', value: status.tier.toUpperCase() },
                  { label: 'Provider', value: status.provider ?? 'Shared Cloud' },
                  { label: 'Status', value: status.status },
                ].map(item => (
                  <div key={item.label} style={{ padding: '12px', background: 'var(--body-bg)', borderRadius: '6px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '4px' }}>{item.label}</div>
                    <div style={{ fontSize: '14px', fontWeight: 600 }}>{item.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Download size={16} color="#3b82f6" />
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Python CLI</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Use the AiCloud CLI for advanced operations, local scrapers, and direct cloud management.
            </p>
            <Link to="/aicloud/cli"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#3b82f6', color: 'white', borderRadius: '6px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
              <Download size={13} /> Download CLI
            </Link>
          </div>

          <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <Download size={16} color="#10b981" />
              <span style={{ fontSize: '14px', fontWeight: 600 }}>Export Data</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
              Export all your AiCloud metadata (projects, agents, configurations). Your cloud data stays in your cloud.
            </p>
            <Link to="/aicloud/export"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: 'transparent', color: '#10b981', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '6px', textDecoration: 'none', fontSize: '13px', fontWeight: 600 }}>
              Export JSON
            </Link>
          </div>

          {status && (
            <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                <Trash2 size={16} color="#ef4444" />
                <span style={{ fontSize: '14px', fontWeight: 600, color: '#ef4444' }}>Disconnect AiCloud</span>
              </div>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
                Removes the connection between RunAI and your cloud account. Your cloud resources will NOT be deleted.
              </p>
              <button onClick={() => setShowConfirm(true)} disabled={disconnecting}
                style={{ padding: '8px 14px', background: 'transparent', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '6px', cursor: disconnecting ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: disconnecting ? 0.6 : 1 }}>
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
